'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { ChevronDown, CheckCircle2, XCircle, ArrowRight, Clock, FileQuestion, LayoutGrid, Shield, Award, Star, Trophy, Globe, Download, Brain } from 'lucide-react';
import type { OlympiadInfo, SyllabusUnit, SampleQuestion } from '@/app/olympiad/data/olympiadData';

interface Props {
  data: OlympiadInfo;
}

const difficultyColors: Record<string, string> = {
  Beginner: 'bg-green-50 text-green-700',
  Intermediate: 'bg-blue-50 text-blue-700',
  Advanced: 'bg-purple-50 text-purple-700',
};

function SyllabusSection({ syllabus }: { syllabus: SyllabusUnit[] }) {
  const [open, setOpen] = useState<string | null>(syllabus[0]?.id ?? null);
  return (
    <div className="max-w-4xl mx-auto space-y-3">
      {syllabus.map((unit) => (
        <div key={unit.id} className="bg-card border border-border rounded-xl shadow-card overflow-hidden">
          <button
            className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-muted/50 transition-colors"
            onClick={() => setOpen(open === unit.id ? null : unit.id)}
          >
            <span className="text-sm font-bold text-foreground">{unit.unit}</span>
            <div className="flex items-center gap-3">
              <span className="text-xs text-muted-foreground">{unit.topics.length} topics</span>
              <ChevronDown size={16} className={`text-muted-foreground transition-transform duration-200 ${open === unit.id ? 'rotate-180' : ''}`} />
            </div>
          </button>
          {open === unit.id && (
            <div className="px-6 pb-5">
              <div className="space-y-3">
                {unit.topics.map((topic, i) => (
                  <div key={`${unit.id}-topic-${i}`} className="border border-border rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-foreground">{topic.name}</span>
                      <span className={`px-2 py-0.5 text-xs font-semibold rounded-md ${difficultyColors[topic.difficulty]}`}>{topic.difficulty}</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {topic.subtopics.map((sub, si) => (
                        <span key={`sub-${si}`} className="px-2 py-0.5 text-xs bg-muted text-muted-foreground rounded-md">{sub}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function SampleQuestionsSection({ questions }: { questions: SampleQuestion[] }) {
  const [selected, setSelected] = useState<Record<string, number>>({});

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {questions.map((q, qi) => {
        const sel = selected[q.id];
        const isCorrect = sel === q.correct;
        return (
          <div key={q.id} className="bg-card border border-border rounded-2xl p-6 shadow-card">
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <span className="px-2 py-0.5 text-xs font-semibold bg-muted text-muted-foreground rounded-md">Q{qi + 1}</span>
              <span className="px-2 py-0.5 text-xs font-semibold bg-secondary/10 text-secondary rounded-md">{q.topic}</span>
              <span className={`px-2 py-0.5 text-xs font-semibold rounded-md ${difficultyColors[q.difficulty]}`}>{q.difficulty}</span>
            </div>
            <p className="text-sm font-medium text-foreground mb-4 leading-relaxed">{q.text}</p>
            <div className="space-y-2 mb-4">
              {q.options.map((opt, oi) => {
                let optClass = 'border-border bg-muted/30 hover:bg-muted hover:border-secondary/50 cursor-pointer';
                if (sel !== undefined) {
                  if (oi === q.correct) optClass = 'border-green-400 bg-green-50 cursor-default';
                  else if (oi === sel && !isCorrect) optClass = 'border-red-400 bg-red-50 cursor-default';
                  else optClass = 'border-border bg-muted/20 opacity-60 cursor-default';
                }
                return (
                  <button
                    key={`${q.id}-opt-${oi}`}
                    className={`w-full flex items-center gap-3 px-4 py-3 border rounded-xl text-left transition-all duration-150 ${optClass}`}
                    onClick={() => { if (sel === undefined) setSelected((p) => ({ ...p, [q.id]: oi })); }}
                    disabled={sel !== undefined}
                  >
                    <span className="w-6 h-6 rounded-full border border-current flex items-center justify-center text-xs font-bold flex-shrink-0">
                      {String.fromCharCode(65 + oi)}
                    </span>
                    <span className="text-sm text-foreground">{opt}</span>
                    {sel !== undefined && oi === q.correct && <CheckCircle2 size={16} className="ml-auto text-green-600 flex-shrink-0" />}
                    {sel !== undefined && oi === sel && !isCorrect && <XCircle size={16} className="ml-auto text-red-500 flex-shrink-0" />}
                  </button>
                );
              })}
            </div>
            {sel !== undefined && (
              <div className={`rounded-xl p-4 text-sm ${isCorrect ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
                <p className={`font-semibold mb-1 ${isCorrect ? 'text-green-700' : 'text-amber-700'}`}>
                  {isCorrect ? '✓ Correct!' : "→ Not quite — here's the explanation:"}
                </p>
                <p className={isCorrect ? 'text-green-600' : 'text-amber-600'}>{q.explanation}</p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function OlympiadDetailPage({ data }: Props) {
  const availableAges = Object.keys(data.ageData).map(Number).sort((a, b) => a - b);
  const [selectedAge, setSelectedAge] = useState<number>(availableAges[0] ?? data.ages[0]);
  const [faqOpen, setFaqOpen] = useState<string | null>(null);

  const currentAgeData = data.ageData[selectedAge];

  return (
    <>
      {/* Hero */}
      <section className="bg-hero-gradient relative overflow-hidden py-20">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-10 w-80 h-80 rounded-full bg-purple-500 blur-3xl" />
          <div className="absolute bottom-10 left-10 w-60 h-60 rounded-full bg-secondary blur-3xl" />
        </div>
        <div className="relative max-w-screen-2xl mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-2 mb-6">
            <Link href="/" className="text-slate-400 text-sm hover:text-white transition-colors">Home</Link>
            <span className="text-slate-600">/</span>
            <Link href="/#olympiads" className="text-slate-400 text-sm hover:text-white transition-colors">Olympiads</Link>
            <span className="text-slate-600">/</span>
            <span className="text-white text-sm">{data.short}</span>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className={`inline-flex items-center gap-2.5 px-3 py-1.5 border rounded-full text-xs font-semibold mb-5 ${data.badgeColor}`}>
                {data.short} Excellence Olympiad
              </div>
              <h1 className="text-4xl xl:text-5xl font-extrabold text-white leading-tight mb-4">
                {data.name}
              </h1>
              <p className="text-lg text-slate-300 leading-relaxed mb-6">
                {data.tagline}
              </p>
              <div className="grid grid-cols-2 gap-3 mb-8">
                {[
                  { icon: Clock, label: 'Duration', value: '60 Minutes' },
                  { icon: FileQuestion, label: 'Questions', value: '50 MCQ' },
                  { icon: Globe, label: 'Exam Availability', value: 'Anytime' },
                  { icon: Shield, label: 'Attempts', value: '2 / year' },
                ]?.map((item) => (
                  <div key={`hero-meta-${item?.label}`} className="flex items-center gap-2.5 bg-white/5 border border-white/10 rounded-xl px-3 py-2.5">
                    <item.icon size={14} className="text-secondary flex-shrink-0" />
                    <div>
                      <p className="text-xs text-slate-400">{item?.label}</p>
                      <p className="text-sm font-semibold text-white">{item?.value}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex gap-3">
                <Link href="/sign-up-login-screen" className="inline-flex items-center gap-2 px-6 py-3.5 bg-secondary text-white font-semibold rounded-xl hover:bg-secondary/90 transition-all active:scale-95">
                  Register Now — ₹200 <ArrowRight size={16} />
                </Link>
                <a href="#syllabus" className="inline-flex items-center gap-2 px-6 py-3.5 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors">
                  View Syllabus
                </a>
              </div>
            </div>
            <div className="hidden lg:block">
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                <p className="text-xs text-slate-400 font-semibold uppercase tracking-wide mb-4">2026 Leaderboard Preview</p>
                <div className="space-y-3">
                  {[
                    { rank: 1, name: 'A**** M.', country: '🇮🇳', score: '49/50', pct: '99.8' },
                    { rank: 2, name: 'Li Wei', country: '🇨🇳', score: '48/50', pct: '99.6' },
                    { rank: 3, name: 'Lena M.', country: '🇩🇪', score: '47/50', pct: '99.3' },
                    { rank: 4, name: 'Priya K.', country: '🇮🇳', score: '46/50', pct: '98.9' },
                    { rank: 5, name: 'Omar A.', country: '🇸🇦', score: '45/50', pct: '98.5' },
                  ]?.map((entry) => (
                    <div key={`lb-${entry?.rank}`} className="flex items-center gap-3 py-2 border-b border-white/5 last:border-0">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${entry?.rank <= 3 ? 'bg-accent text-white' : 'bg-white/10 text-slate-300'}`}>{entry?.rank}</span>
                      <span className="text-sm text-white font-medium flex-1">{entry?.country} {entry?.name}</span>
                      <span className="text-xs font-mono text-slate-300">{entry?.score}</span>
                      <span className="text-xs font-mono text-secondary">{entry?.pct}%ile</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Exam Pattern */}
      <section className="py-16 bg-background border-b border-border">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-10">
            {[
              { icon: FileQuestion, label: 'Total Questions', value: '50', sub: 'MCQ format' },
              { icon: Clock, label: 'Time Limit', value: '60 min', sub: '72 sec per question' },
              { icon: LayoutGrid, label: 'Question Types', value: '3 Types', sub: 'MCQ · Scenario · Logic' },
              { icon: Shield, label: 'Attempts Allowed', value: '2 / year', sub: 'Best score counts' },
            ]?.map((item) => (
              <div key={`pattern-${item?.label}`} className="bg-card border border-border rounded-2xl p-5 text-center shadow-card">
                <div className="w-10 h-10 bg-secondary/10 text-secondary rounded-xl flex items-center justify-center mx-auto mb-3">
                  <item.icon size={18} />
                </div>
                <p className="text-xl font-bold font-mono text-foreground">{item?.value}</p>
                <p className="text-sm font-semibold text-foreground mt-0.5">{item?.label}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{item?.sub}</p>
              </div>
            ))}
          </div>
          <div className="bg-muted/50 rounded-2xl p-6 border border-border">
            <h3 className="text-sm font-bold text-foreground mb-4">Marks & Scoring</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              {[
                { label: 'Wrong Answer', value: '0 marks', color: 'text-muted-foreground bg-muted', note: 'No negative marking' },
                { label: 'Not Attempted', value: '0 marks', color: 'text-muted-foreground bg-muted', note: 'No penalty' },
              ]?.map((item) => (
                <div key={`score-${item?.label}`} className="flex items-center justify-between bg-card rounded-xl px-4 py-3 border border-border">
                  <div>
                    <span className="text-sm font-medium text-foreground">{item?.label}</span>
                    <p className="text-xs text-muted-foreground mt-0.5">{item?.note}</p>
                  </div>
                  <span className={`px-2.5 py-1 text-sm font-bold font-mono rounded-lg ${item?.color}`}>{item?.value}</span>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { label: 'Easy Question', value: '+1 mark', color: 'text-green-600 bg-green-50' },
                { label: 'Medium Question', value: '+2 marks', color: 'text-blue-600 bg-blue-50' },
                { label: 'Hard Question', value: '+3 marks', color: 'text-purple-600 bg-purple-50' },
              ]?.map((item) => (
                <div key={`score-${item?.label}`} className="flex items-center justify-between bg-card rounded-xl px-4 py-3 border border-border">
                  <span className="text-sm font-medium text-foreground">{item?.label}</span>
                  <span className={`px-2.5 py-1 text-sm font-bold font-mono rounded-lg ${item?.color}`}>{item?.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Syllabus with Grade Selector */}
      <section id="syllabus" className="py-20 bg-muted/40">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-extrabold text-foreground mb-3">Detailed Syllabus</h2>
            <p className="text-muted-foreground max-w-xl mx-auto mb-6">
              Select your grade to view the syllabus tailored for your level. Each grade has its own dedicated test.
            </p>
            {/* Grade Selector */}
            <div className="inline-flex items-center gap-3 bg-card border border-border rounded-xl px-4 py-3 shadow-card">
              <label className="text-sm font-semibold text-foreground whitespace-nowrap">Select Grade:</label>
              <select
                value={selectedAge}
                onChange={(e) => setSelectedAge(Number(e.target.value))}
                className="text-sm font-medium text-foreground bg-transparent border-none outline-none cursor-pointer pr-2"
              >
                {data.ages.map((age) => (
                  <option key={age} value={age}>Grade {age}</option>
                ))}
              </select>
            </div>
            {!currentAgeData && (
              <p className="mt-4 text-sm text-muted-foreground">Detailed syllabus for age {selectedAge} coming soon. Showing closest available content.</p>
            )}
          </div>

          {currentAgeData ? (
            <SyllabusSection syllabus={currentAgeData.syllabus} />
          ) : (
            <div className="max-w-4xl mx-auto">
              <SyllabusSection syllabus={Object.values(data.ageData)[0]?.syllabus ?? []} />
            </div>
          )}
        </div>
      </section>

      {/* Sample Questions */}
      <section className="py-20 bg-background">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-extrabold text-foreground mb-3">Sample Questions — Grade {selectedAge}</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Try these interactive sample questions to understand the exam format. Questions test conceptual understanding and scenario & logic based reasoning.
            </p>
          </div>
          {currentAgeData ? (
            <SampleQuestionsSection questions={currentAgeData.sampleQuestions} />
          ) : (
            <SampleQuestionsSection questions={Object.values(data.ageData)[0]?.sampleQuestions ?? []} />
          )}
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-muted/40">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-extrabold text-foreground mb-3">Rewards & Recognition</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">What you get for participating — and excelling — in the {data.short} Olympiad.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto">
            {[
              { id: 'rew-1', icon: Award, title: 'Participation Certificate', desc: 'Every student who completes the exam receives a verifiable digital certificate with a unique ID — generated instantly and sent to your email.', color: 'bg-blue-50 text-blue-600' },
              { id: 'rew-2', icon: Star, title: 'Merit Certificate (Top 10%)', desc: 'Students scoring in the top 10th percentile receive a distinguished merit certificate.', color: 'bg-amber-50 text-amber-600' },
              { id: 'rew-3', icon: Trophy, title: 'Gold / Silver / Bronze (Top 1%)', desc: 'Top-ranking students receive tiered recognition certificates with physical medals for top 100 globally.', color: 'bg-yellow-50 text-yellow-600' },
              { id: 'rew-4', icon: Globe, title: 'Global Leaderboard Listing', desc: 'All students appear on the live global leaderboard — with privacy controls to stay anonymous.', color: 'bg-green-50 text-green-600' },
              { id: 'rew-5', icon: Brain, title: 'Instant AI Report Card', desc: 'Receive your detailed report instantly — topic-wise performance breakdown with AI-generated insights and improvement recommendations.', color: 'bg-purple-50 text-purple-600' },
              { id: 'rew-6', icon: Download, title: 'Shareable Digital Badge', desc: 'LinkedIn-ready digital badge and shareable result card for social media.', color: 'bg-teal-50 text-teal-600' },
            ]?.map((item) => (
              <div key={item?.id} className="bg-card border border-border rounded-2xl p-5 shadow-card">
                <div className={`w-10 h-10 rounded-xl ${item?.color} flex items-center justify-center mb-3`}>
                  <item.icon size={18} />
                </div>
                <h3 className="text-sm font-bold text-foreground mb-1.5">{item?.title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{item?.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-background">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-2xl font-extrabold text-foreground mb-8 text-center">Questions about the {data.short} Olympiad</h2>
          <div className="space-y-3">
            {data.faqs?.map((faq) => (
              <div key={faq?.id} className="bg-card border border-border rounded-xl overflow-hidden">
                <button
                  className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-muted/50 transition-colors"
                  onClick={() => setFaqOpen(faqOpen === faq?.id ? null : faq?.id)}
                >
                  <span className="text-sm font-semibold text-foreground pr-4">{faq?.q}</span>
                  <ChevronDown size={16} className={`flex-shrink-0 text-muted-foreground transition-transform duration-200 ${faqOpen === faq?.id ? 'rotate-180' : ''}`} />
                </button>
                {faqOpen === faq?.id && (
                  <div className="px-5 pb-4">
                    <p className="text-sm text-muted-foreground leading-relaxed">{faq?.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-hero-gradient relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-secondary blur-3xl" />
        </div>
        <div className="relative max-w-screen-2xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="text-3xl xl:text-4xl font-extrabold text-white mb-4">
            Ready to Benchmark Your {data.subject} Skills?
          </h2>
          <p className="text-slate-300 text-lg max-w-xl mx-auto mb-8">
            Join 120,000+ students from 40+ countries. Registration is open for the 2026 season.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link href="/sign-up-login-screen" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-primary font-bold rounded-xl hover:bg-slate-50 transition-all active:scale-95">
              Register Now — ₹200 <ArrowRight size={16} />
            </Link>
            <Link href="/student-dashboard" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors border border-white/20">
              View Student Dashboard
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
